/**
 * =============================================================================
 * MISSION CONTROL LAYOUT COMPONENT - AI-NATIVE DESIGN SYSTEM
 * =============================================================================
 * 
 * Renders a Mission Control page based on a layout configuration.
 * 
 * DESIGN SYSTEM:
 * - 8pt spacing grid (8, 16, 24, 32px)
 * - Semantic colors only (no decorative use)
 * - Typography hierarchy: Page 28-32px, Section 18-20px, Card 16-18px
 * - Action-driven, not report-driven
 * - Calm, modern AI-native aesthetic
 */

import AskAiButton from '@components/QuickHelpPanel/AskAiButton';
import { Box, Stack, Text, Group } from '@mantine/core';
import { memo, useMemo, type FC } from 'react';
import type { 
    MissionControlLayoutProps, 
    LayoutRegion, 
    WidgetConfig,
    PageLayoutConfig,
} from '../types';

// Import widgets
import { 
    MetricStrip, 
    WatchCardGrid, 
    StandaloneWatchCard, 
    WatchlistWidget,
    AISpotlightWidget,
} from '../widgets';

// =============================================================================
// DESIGN TOKENS (8pt grid)
// =============================================================================
const spacing = {
    xs: 8,
    sm: 16,
    md: 24,
    lg: 32,
    xl: 40,
    xxl: 48,
} as const;

// =============================================================================
// HELPER: Get greeting based on time
// =============================================================================
const getGreeting = (): string => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
};

// =============================================================================
// HELPER: Get user's first name from localStorage
// =============================================================================
const getUserFirstName = (): string => {
    try {
        const userJson = localStorage.getItem('user');
        if (userJson) {
            const user = JSON.parse(userJson);
            return user.firstName || '';
        }
    } catch {
        // Ignore errors
    }
    return '';
};

// =============================================================================
// WIDGET RENDERER
// =============================================================================
interface WidgetRendererProps {
    widget: WidgetConfig;
}

const WidgetRenderer: FC<WidgetRendererProps> = memo(({ widget }) => {
    switch (widget.type) {
        case 'metric-strip':
            return (
                <MetricStrip
                    tagline={widget.tagline}
                    instances={widget.instances}
                />
            );
        
        case 'watch-card-grid':
            return (
                <WatchCardGrid
                    tagline={widget.tagline}
                    title={widget.title}
                    cards={widget.cards}
                    columns={widget.columns}
                    rows={widget.rows}
                />
            );
        
        case 'standalone-watch-card':
            return (
                <StandaloneWatchCard
                    tagline={widget.tagline}
                    config={widget.config}
                    isFeatured={widget.isFeatured}
                />
            );
        
        case 'watchlist':
            return (
                <WatchlistWidget
                    config={widget.config}
                />
            );
        
        case 'ai-spotlight-widget':
            return (
                <AISpotlightWidget
                    tagline={widget.tagline}
                    config={widget.config}
                />
            );
        
        case 'custom':
            // Render nested children with flex distribution
            return (
                <Stack className="h-full" style={{ gap: spacing.md }}>
                    {widget.children.map((child) => (
                        <Box
                            key={child.id}
                            style={{
                                // A weighted child grows into spare height but never shrinks
                                // below its content: on a short viewport the page gets taller
                                // and scrolls instead of the widget being clipped.
                                flex: child.height === 'auto' ? '0 0 auto' : `${child.height} 0 auto`,
                                width: child.width === 'auto' ? 'auto' : `${child.width}%`,
                            }}
                        >
                            <WidgetRenderer widget={child.widget} />
                        </Box>
                    ))}
                </Stack>
            );
        
        default:
            return (
                <Box className="flex items-center justify-center h-full rounded-lg bg-slate-50">
                    <Text className="text-slate-400 text-sm">Unknown widget type</Text>
                </Box>
            );
    }
});

WidgetRenderer.displayName = 'WidgetRenderer';

// =============================================================================
// REGION RENDERER
// =============================================================================
interface RegionRendererProps {
    region: LayoutRegion;
    /** The row's column gap, so percentage widths can absorb it instead of overflowing. */
    gap: number;
}

/**
 * Below this width a region wraps onto its own line rather than squeezing. Two side-by-side
 * regions therefore stack once the row is narrower than roughly twice this.
 */
const REGION_MIN_WIDTH = 400;

const RegionRenderer: FC<RegionRendererProps> = memo(({ region, gap }) => {
    const style: React.CSSProperties = {
        // Percentage widths are flex bases net of the gap, so 55% + 45% + gap still fits one
        // row. Each region can shrink to REGION_MIN_WIDTH and then wraps (the row wraps).
        ...(region.width === 'auto'
            ? { width: 'auto' }
            : {
                  flex: `1 1 calc(${region.width}% - ${gap / 2}px)`,
                  minWidth: `min(100%, ${REGION_MIN_WIDTH}px)`,
              }),
        // No explicit height: the row's align-items: stretch gives every region the row's full
        // height. A `height: 100%` here resolves against an indefinite row and blocks that
        // stretch, which collapsed the watchlist to its header.
        height: 'auto',
        ...(region.padding && {
            paddingTop: region.padding.top,
            paddingRight: region.padding.right,
            paddingBottom: region.padding.bottom,
            paddingLeft: region.padding.left,
        }),
    };
    
    return (
        <Box style={style} className={`${region.className || ''} flex flex-col`}>
            <WidgetRenderer widget={region.widget} />
        </Box>
    );
});

RegionRenderer.displayName = 'RegionRenderer';

// =============================================================================
// HEADER COMPONENT - Minimal, action-oriented
// =============================================================================
interface HeaderProps {
    config: PageLayoutConfig['header'];
}

const Header: FC<HeaderProps> = memo(({ config }) => {
    const greeting = useMemo(() => getGreeting(), []);
    const firstName = useMemo(() => getUserFirstName(), []);
    
    return (
        <Box style={{ marginBottom: spacing.lg }}>
            <Group className="justify-between items-end">
                <Group style={{ gap: 12 }}>
                    {/* Icon container - consistent with other pages */}
                    <Box
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 40,
                            height: 40,
                            borderRadius: 12,
                            backgroundColor: 'rgba(0,0,0,0.03)',
                            fontSize: 20,
                        }}
                    >
                        🎯
                    </Box>
                    
                    <Stack style={{ gap: 4 }}>
                        {/* Page Title - 28px */}
                        <Text 
                            className="text-slate-900"
                            style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.2 }}
                        >
                            {config.title}
                        </Text>
                        
                        {/* Greeting - Secondary, muted - 14px */}
                        {config.showGreeting && (
                            <Text className="text-slate-500" style={{ fontSize: 14 }}>
                                {greeting}{config.showUserName && firstName ? `, ${firstName}` : ''}
                            </Text>
                        )}
                    </Stack>
                </Group>
                
                <Group style={{ gap: 16, alignItems: 'center' }}>
                    <AskAiButton />
                    {/* Timestamp - subtle right-aligned info */}
                    <Text className="text-slate-400" style={{ fontSize: 12 }}>
                        {new Date().toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'short',
                            day: 'numeric'
                        })}
                    </Text>
                </Group>
            </Group>
        </Box>
    );
});

Header.displayName = 'Header';

// =============================================================================
// SECTION HEADER COMPONENT - 18-20px
// =============================================================================
interface SectionHeaderProps {
    title: string;
    subtitle?: string;
    count?: number;
    severity?: 'critical' | 'warning' | 'info' | 'positive';
}

export const SectionHeader: FC<SectionHeaderProps> = memo(({ 
    title, 
    subtitle,
    count,
    severity 
}) => {
    const severityColor = severity === 'critical' ? 'rgb(205, 101, 101)' :
                          severity === 'warning' ? 'rgb(219, 182, 88)' :
                          severity === 'positive' ? 'rgb(45, 187, 136)' :
                          'rgb(1, 149, 255)';
    
    return (
        <Group className="items-center" style={{ gap: spacing.sm, marginBottom: spacing.sm }}>
            {/* Severity indicator */}
            {severity && (
                <Box 
                    style={{ 
                        width: 4, 
                        height: 24, 
                        borderRadius: 2,
                        backgroundColor: severityColor 
                    }} 
                />
            )}
            
            <Stack style={{ gap: 2 }}>
                <Group style={{ gap: spacing.xs }}>
                    <Text 
                        className="font-semibold text-slate-900"
                        style={{ fontSize: 18, lineHeight: 1.3 }}
                    >
                        {title}
                    </Text>
                    {count !== undefined && count > 0 && (
                        <Text 
                            className="text-slate-400 font-medium"
                            style={{ fontSize: 14 }}
                        >
                            ({count})
                        </Text>
                    )}
                </Group>
                {subtitle && (
                    <Text className="text-slate-400" style={{ fontSize: 13 }}>
                        {subtitle}
                    </Text>
                )}
            </Stack>
        </Group>
    );
});

SectionHeader.displayName = 'SectionHeader';

// =============================================================================
// MAIN LAYOUT COMPONENT
// =============================================================================
const MissionControlLayout: FC<MissionControlLayoutProps> = memo(({ 
    config, 
    className = '' 
}) => {
    return (
        // min-h-full, not h-full: the dashboard fills the viewport when it fits and grows past it
        // when it does not, so the Page shell's own scroll takes over on smaller screens. Nothing
        // in here clips — only the watchlist scrolls internally, bounded by the row it sits in.
        <Box
            className={`min-h-full w-full flex flex-col ${className}`}
            style={{
                backgroundColor: '#FAFAFA',
                padding: `${spacing.lg}px ${spacing.lg}px`,
            }}
        >
            {/* Header */}
            <Header config={config.header} />

            {/* Layout rows */}
            <Stack className="flex-1" style={{ gap: spacing.md }}>
                {config.rows.map((row) => {
                    const gap = row.gap || spacing.md;
                    return (
                        <Box
                            key={row.id}
                            style={{
                                // A weighted row grows into spare height but never shrinks
                                // below its content (see WidgetRenderer's custom branch).
                                flex: row.height === 'auto' ? '0 0 auto' : '1 0 auto',
                            }}
                            className={row.className || ''}
                        >
                            <Group
                                className="h-full items-stretch"
                                style={{ gap }}
                                wrap="wrap"
                                align="stretch"
                            >
                                {row.regions.map((region) => (
                                    <RegionRenderer key={region.id} region={region} gap={gap} />
                                ))}
                            </Group>
                        </Box>
                    );
                })}
            </Stack>
        </Box>
    );
});

MissionControlLayout.displayName = 'MissionControlLayout';

export default MissionControlLayout;
